import { lazy, Suspense } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import {
  FileText, Database, Store, TrendingUp,
  Calendar, BookMarked,
} from "lucide-react";
import MainLayout from "./layout/MainLayout";
import SidebarLayout from "./layout/SidebarLayout";
import AppShell from "./layout/AppShell";
import ProtectedRoute from "./components/ProtectedRoute";
import OnboardingRequiredRoute from "./components/OnboardingRequiredRoute";

const Home = lazy(() => import("./pages/Home"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const FeedPage = lazy(() => import("./pages/FeedPage"));
const FeedPostRoute = lazy(() => import("./components/FeedPostRoute"));
const ComingSoonPage = lazy(() => import("./pages/ComingSoonPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const VerifyEmailPage = lazy(() => import("./pages/VerifyEmailPage"));
const OnboardingWizardPage = lazy(() => import("./pages/OnboardingWizardPageNew"));
const WhyVerifyPage = lazy(() => import("./pages/WhyVerifyPage"));
const EmploymentVerificationSubmitPage = lazy(() => import("./pages/EmploymentVerificationSubmitPage"));
const EmploymentVerificationConfirmPage = lazy(() => import("./pages/EmploymentVerificationConfirmPage"));
const PublicDocsPage = lazy(() => import("./pages/PublicDocsPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const SignupSuccessPage = lazy(() => import("./pages/SignupSuccessPage"));
const ActivateAccountPage = lazy(() => import("./pages/ActivateAccountPage"));
const ReactivateAccountPage = lazy(() => import("./pages/ReactivateAccountPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const SurveysPage = lazy(() => import("./pages/SurveysPage"));
const SurveyBuilderPage = lazy(() => import("./pages/SurveyBuilderPage"));
const SurveyDetailPage = lazy(() => import("./pages/SurveyDetailPage"));
const SurveyRespondPage = lazy(() => import("./pages/SurveyRespondPage"));

const RouteFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-gray-50">
    <div className="h-8 w-8 rounded-full border-[3px] border-gray-200 border-t-primary animate-spin" />
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/" element={<MainLayout />}>
              {/* Public routes */}
              <Route index element={<Home />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/signup-success" element={<SignupSuccessPage />} />
              <Route path="/activate-account" element={<ActivateAccountPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/docs" element={<PublicDocsPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/terms-of-service" element={<TermsOfServicePage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/reactivate-account" element={<ReactivateAccountPage />} />

              {/* Shareable post link — auth-aware: logged-in users get the
                  full sidebar + interactions, logged-out visitors get a
                  read-only view with a signup CTA. */}
              <Route path="/feed/:postId" element={<FeedPostRoute />} />

              {/* Notifications - requires login only; wrapped in AppShell
                  so mobile users get the bottom nav and desktop users the
                  sidebar, without forcing onboarding. */}
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <AppShell>
                      <NotificationsPage />
                    </AppShell>
                  </ProtectedRoute>
                }
              />

              {/* Onboarding - requires login */}
              <Route
                path="/onboarding"
                element={
                  <ProtectedRoute>
                    <OnboardingWizardPage />
                  </ProtectedRoute>
                }
              />

              {/* App pages — require login + onboarding, share the sidebar layout */}
              <Route
                element={
                  <ProtectedRoute>
                    <OnboardingRequiredRoute>
                      <SidebarLayout />
                    </OnboardingRequiredRoute>
                  </ProtectedRoute>
                }
              >
                <Route path="/feed"      element={<FeedPage />} />
                <Route path="/documents" element={<ComingSoonPage icon={FileText}       title="Documents"  description="A central hub for all your HR documents, templates, and policies — organised, searchable, and shareable with your team." detail="We're building a powerful document management experience tailored for HR professionals." />} />
                <Route path="/database"  element={<ComingSoonPage icon={Database}       title="Database"   description="A structured database of HR data, employee records, and workforce analytics to support better decision-making." detail="Query and manage your HR data in one place, with privacy built in." hideBadge><div className="text-sm text-gray-600 leading-relaxed space-y-3 bg-gray-50 border border-gray-200 rounded-xl p-4 text-left"><p>Access the documents here in the first database: <a href="https://2ly.link/1ycv3" target="_blank" rel="noopener noreferrer" className="text-[#CA8A04] underline hover:text-[#a16207] font-medium">Open first database</a></p><p>If you don't have access to the first, try the second: <a href="https://2ly.link/25nlh" target="_blank" rel="noopener noreferrer" className="text-[#CA8A04] underline hover:text-[#a16207] font-medium">Open second database</a></p><p className="text-xs text-gray-400 italic">Note: It's the same content in both. Having access to one is sufficient.</p></div></ComingSoonPage>} />
                <Route path="/vendors"   element={<ComingSoonPage icon={Store}          title="Vendors"    description="Browse and compare HR software vendors, service providers, and consultants reviewed by your peers." detail="Community-driven, unbiased vendor reviews from HR professionals like you." />} />
                <Route path="/insights"  element={<ComingSoonPage icon={TrendingUp}     title="Insights"   description="Data-driven trends, benchmarks, and industry insights curated specifically for HR professionals." detail="Stay ahead with real-time data on hiring, compensation, retention, and more." />} />
                <Route path="/surveys"             element={<SurveysPage />} />
                <Route path="/surveys/create"     element={<SurveyBuilderPage />} />
                <Route path="/surveys/:id"        element={<SurveyDetailPage />} />
                <Route path="/surveys/:id/edit"   element={<SurveyBuilderPage />} />
                <Route path="/surveys/:id/respond" element={<SurveyRespondPage />} />
                <Route path="/events"    element={<ComingSoonPage icon={Calendar}       title="Events"     description="Discover HR conferences, webinars, workshops, and networking events happening near you and online." detail="Never miss a learning or networking opportunity in the HR space." />} />
                <Route path="/resources" element={<ComingSoonPage icon={BookMarked}     title="Resources"  description="A curated library of HR frameworks, legal guides, research papers, and best-practice playbooks." detail="Everything you need to stay informed and effective in your role." />} />
                <Route path="/settings"  element={<Navigate to="/profile?tab=settings" replace />} />
              </Route>

              {/* Why Verify - public page */}
              <Route path="/why-verify" element={<WhyVerifyPage />} />

              {/* Employment verification - requires login + onboarding */}
              <Route
                path="/employment-verification"
                element={
                  <ProtectedRoute>
                    <OnboardingRequiredRoute>
                      <EmploymentVerificationSubmitPage />
                    </OnboardingRequiredRoute>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/employment-verify"
                element={
                  <ProtectedRoute>
                    <OnboardingRequiredRoute>
                      <EmploymentVerificationConfirmPage />
                    </OnboardingRequiredRoute>
                  </ProtectedRoute>
                }
              />

              {/* Profile - requires login + onboarding */}
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <OnboardingRequiredRoute>
                      <ProfilePage />
                    </OnboardingRequiredRoute>
                  </ProtectedRoute>
                }
              />

              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Suspense>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
